/** REZUMATUL DE BUSINESS CASE VINE DE LA SERVER; AICI SE SCRIE.
 *
 *  Socoteala e a serviciului de planificare (`cost_model.business_case`,
 *  prin `control-plane/api/business_case.py`): el adună faptele proiectului
 *  (dosarul, soluțiile firului, documentul planului, devizele, lista,
 *  programul) și întoarce secțiunile cu rândurile lor. Textele rândurilor
 *  vin ca JETOANE — cheile catalogului (`bc.*`) și cifrele, nu șiruri —,
 *  fiindcă serverul nu știe nici limba, nici moneda ecranului. Aici se scriu:
 *  `scrie` un jeton, `dinServer` un rezumat întreg, în forma pe care o
 *  desenează panourile (`Rand`, `Rezumat`).
 *
 *  Un jeton (v. `cost_model/business_case/text.py`):
 *    "…"                    — un șir luat ca atare;
 *    {t, p?}                — `t(cheie, parametri)`, parametrii pot fi jetoane;
 *    {tn, n, p?}            — forma de plural;
 *    {n, z}                 — un număr cu `z` zecimale;
 *    {ani}                  — un număr cu cel mult o zecimală;
 *    {eur, z}               — o sumă în EUR, scrisă în moneda ecranului;
 *    {pe, um, z}            — un preț unitar, «650 €/m²»;
 *    {pc, z}                — un procent din fracție, «16 %»;
 *    {moneda}               — simbolul monedei ecranului;
 *    [...]                  — bucățile una după alta.
 */
import { get, post } from "../../api";
import { numar, t, tn } from "../../i18n";
import type { Cheie, CheiePlural } from "../../i18n";
import { bani, simbolMoneda } from "../../structure/bani";
import type { Detaliu, Ipoteze, Rand, Rezumat, Sectiune, Sursa } from "./businessCase";

export type Jeton =
  | string
  | { t: string; p?: Record<string, Jeton | number | string | null> }
  | { tn: string; n: number; p?: Record<string, Jeton | number | string | null> }
  | { n: number; z: number }
  | { ani: number }
  | { eur: number; z: number }
  | { pe: number; um: Jeton; z: number }
  | { pc: number; z: number }
  | { moneda: true }
  | Jeton[];

const nr = (v: number, z: number) => numar(v, { minimumFractionDigits: z, maximumFractionDigits: z });

/** Jetonul, scris în limba și moneda ecranului. */
export function scrie(j: Jeton | null | undefined): string {
  if (j == null) return "";
  if (typeof j === "string") return j;
  if (Array.isArray(j)) return j.map(scrie).join("");
  if ("t" in j) return t(j.t as Cheie, parametri(j.p));
  if ("tn" in j) return tn(j.tn as CheiePlural, j.n, parametri(j.p));
  if ("n" in j) return nr(j.n, j.z);
  if ("ani" in j) return numar(j.ani, { maximumFractionDigits: 1 });
  if ("eur" in j) return bani(j.eur, { zecimale: j.z });
  if ("pe" in j) return `${bani(j.pe, { zecimale: j.z })}/${scrie(j.um)}`;
  if ("pc" in j) return `${nr(j.pc * 100, j.z)} %`;
  if ("moneda" in j) return simbolMoneda();
  return "";
}

function parametri(p?: Record<string, Jeton | number | string | null>): Record<string, string | number> | undefined {
  if (!p) return undefined;
  const out: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(p)) {
    out[k] = typeof v === "number" ? v : v == null ? "" : typeof v === "string" ? v : scrie(v);
  }
  return out;
}

/** Un rând, cum îl dă serverul: cifrele, plus textele ca jetoane. */
export type RandServer = {
  cheie: string; element: Jeton; cantitate?: number; um?: Jeton; zecimale?: number; procent?: boolean;
  monetar?: boolean; parametru?: Jeton; valoare?: number; observatie?: Jeton; sursa: Sursa; de_unde: Jeton;
  cum_iese: Jeton; semnal?: Jeton; se_schimba_din?: Jeton; grup?: Jeton; subtotal?: boolean; detaliu?: Detaliu;
  in_lucru?: boolean; ajustare?: number;
};
export type SectiuneServer = { cheie: string; titlu: Jeton; randuri: RandServer[]; total?: RandServer };
export type RezumatServer = {
  ipoteze: Ipoteze; sectiuni: SectiuneServer[]; tic: number; tic_pe_mp: number; constructii: number; venituri: number;
  profit_brut: number; profit_net: number; marja: number; roi: number; roi_pe_an: number; prag: number;
  /** banii în timp (`business_case/flux.py`): capitalul maxim imobilizat, capital-anii, RIR */
  capital_max?: number; capital_ani?: number; rir?: number | null; flux_sursa?: "program" | "ipoteza"; flux_luni?: number;
};
/** Profitul net pe banda de prețuri din jurul celui ales. */
export type PunctBanda = { pret: number; net: number; baza: boolean };

/** Devizul structurii, adus la forma rezumatului de server (pentru detaliile de sub rânduri). */
export type StructuraServer = {
  terasamenteDirect: number; structuraDirect: number; infraDirect: number; sapaturaM3: number;
  pretSapaturaM3: number | null; evacuareM3: number; groapaM3: number; ariaMp: number | null; corpuri: number;
  izolate: boolean; groapaComuna: boolean;
};

/** Răspunsul `GET …/business_case` (și al simulării). */
export type RaspunsBusinessCase = {
  scenariu: string | null;
  /** abaterile cu care s-a calculat (ale scenariului, plus cele cerute la simulare) */
  abateri: Record<string, unknown>;
  ipoteze: Ipoteze;
  /** ipotezele proiectului fără abaterile vreunui scenariu */
  implicite: Ipoteze;
  rezumat: RezumatServer;
  banda: PunctBanda[] | null;
  groapa: { dinPlan: boolean; arieMp: number; niveluri: number; adancimeM: number; volumM3: number };
  proiect: {
    structuraInLucru?: boolean; fatadaInLucru?: boolean; are_lista: boolean; are_deviz_structura: boolean;
    are_deviz_fatada: boolean; structura: StructuraServer | null; planReunit?: boolean; programLuni?: number | null;
  } & Record<string, unknown>;
  note: string[];
  lista_generata_la?: string | null;
  durata_s?: number;
};

function rand(r: RandServer): Rand {
  const out: Rand = {
    cheie: r.cheie, element: scrie(r.element), sursa: r.sursa, deUnde: scrie(r.de_unde), cumIese: scrie(r.cum_iese),
  };
  if (r.cantitate != null) out.cantitate = r.cantitate;
  if (r.um != null) out.um = scrie(r.um);
  if (r.zecimale != null) out.zecimale = r.zecimale;
  if (r.procent) out.procent = true;
  if (r.monetar) out.monetar = true;
  if (r.parametru != null) out.parametru = scrie(r.parametru);
  if (r.valoare != null) out.valoare = r.valoare;
  if (r.observatie != null) out.observatie = scrie(r.observatie);
  if (r.semnal != null) out.semnal = scrie(r.semnal);
  if (r.se_schimba_din != null) out.seSchimbaDin = scrie(r.se_schimba_din);
  if (r.grup != null) out.grup = scrie(r.grup);
  if (r.subtotal) out.subtotal = true;
  if (r.detaliu) out.detaliu = r.detaliu;
  if (r.in_lucru != null) out.inLucru = r.in_lucru;
  if (r.ajustare != null) out.ajustare = r.ajustare;
  return out;
}

/** Rezumatul serverului, scris în limba și moneda ecranului. */
export function dinServer(r: RezumatServer): Rezumat {
  const sectiuni: Sectiune[] = r.sectiuni.map((s) => ({
    cheie: s.cheie, titlu: scrie(s.titlu), randuri: s.randuri.map(rand), ...(s.total ? { total: rand(s.total) } : {}),
  }));
  return {
    ipoteze: r.ipoteze, sectiuni, tic: r.tic, ticPeMp: r.tic_pe_mp, constructii: r.constructii,
    venituri: r.venituri, profitBrut: r.profit_brut, profitNet: r.profit_net, marja: r.marja, roi: r.roi,
    roiPeAn: r.roi_pe_an, pragRentabilitate: r.prag,
    capitalMax: r.capital_max ?? null, capitalAni: r.capital_ani ?? null, rir: r.rir ?? null,
    fluxDinProgram: r.flux_sursa === "program",
  };
}

// `/api/…`: căile clientului poartă prefixul (v. `api.ts`); fără el nginx
// răspunde cu pagina aplicației, iar `response.json()` cade pe `<!doctype html>`.
const cale = (projectId: string, solutionId: string) =>
  `/api/projects/${projectId}/solutions/${solutionId}/business_case`;

/** Rezumatul scenariului (lipsă = baza) pe soluția listei. */
export const getBusinessCase = (projectId: string, solutionId: string, opt: { scenariu?: string | null } = {}) =>
  get<RaspunsBusinessCase>(`${cale(projectId, solutionId)}${opt.scenariu ? `?scenariu=${encodeURIComponent(opt.scenariu)}` : ""}`);

/** Rezumatul cu alte abateri peste scenariul dat, fără să salveze nimic. */
export const simuleazaBusinessCase = (projectId: string, solutionId: string,
                                      body: { scenariu?: string | null; abateri: Record<string, unknown> }) =>
  post<RaspunsBusinessCase>(`${cale(projectId, solutionId)}/simuleaza`, body);
